import { Component, Input, OnInit, OnDestroy, ElementRef, AfterViewInit, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ManagedContentService } from '../shared/service/managed-content.service';
import { UserSessionService } from '../shared/service/user-session.service';
import { UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl, FormGroup, ReactiveFormsModule,AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import {CmElement, ContentType, CmTooltip, CmHelp, CmSection} from '../shared/model/cm/managed-content.model';
import { GlobalConstants, COMPANY_ID, EEOI_APP_ROUTES } from '../shared/global-constants';
import { StateRegion } from '../shared/model/state-region';
import { ApplicantMhs } from '../shared/model/session/applicant-mhs';
import { formatDate } from '@angular/common';
import { AddressMhs } from '../shared/model/session/address-mhs';
import { FormUtils } from '../shared/helper/form-utils';
import { MhsService } from '../shared/service/mhs.service';
import { AuthGuardService } from '../shared/service/auth-guard.service';
import { ApplicantType } from '../shared/model/ui/applicant-type';
import { AppInsightsApiService } from '../shared/service/app-insights-api.service';
import { EmployeeMhs } from '../shared/model/session/employee-mhs';
import { fromEvent, Observable, of, Subscription } from 'rxjs';
import { filter, skip } from 'rxjs/operators';
import { WindowRef } from '../shared/service/window.service';
import { ModalService } from '../shared/service/modal.service';
import { AppInsightsLoggerService } from '../shared/service/app-insights-logger.service';
import { GoogleAnalyticsService } from '../shared/service/google-analytics.service';
import { FormControlUtilitiesService } from '../shared/service/form-control-utilities.service';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { ContinueCancelComponent } from '../continue-cancel/continue-cancel.component';
import { PartialSaveService } from '../shared/service/partial-save.service';
import { ApplicationService } from '../shared/service/application.service';
import { PartialSaveResp } from '../shared/model/partial-save-resp';
import { PartialSaveData } from '../shared/model/partial-save-data';
import { environment } from '@environment';

@Component({
  selector: 'app-applicant-info',
  standalone: true,
  imports: [ReactiveFormsModule, ContinueCancelComponent],
  templateUrl: './applicant-info.component.html',
  styleUrls: ['./applicant-info.component.scss'],
  providers: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ApplicantInfoComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() cancelEmitter = new EventEmitter<void>();
  @Output() backEmitter = new EventEmitter<void>();
  @Output() val = new EventEmitter<boolean>();
  @Input() parentForm: UntypedFormGroup;
  @Input() showBackButton: boolean = false;

  /**
  * True when the user reached /applicant-information via the review
   * Overview "View" button (URL ?mode=edit or history.state.editApplicant).
   * In that mode the page shows Back / Save buttons instead of Continue / Cancel
   * and the Save button persists the changes then returns to /review.
   */
  public isEditFromReview: boolean = false;

  /**
   * True while an edit-mode save round-trip is in flight. Used to disable
   * the Save / Back buttons so a double-click cannot trigger duplicate
   * backend writes or a navigation race.
   */
  public isSavingEdit: boolean = false;
  /** Set to true when the user clicks Back without saving so ngOnDestroy
   *  does not overwrite the session cache with unsaved (possibly empty) form data. */
  private discardFormDataOnDestroy: boolean = false;

  resizeObservable$: Observable<Event>;
  resizeSubscription$: Subscription;
  public innerWidth: number = window.innerWidth;
  public birthplaceTooltipPosition: string = 'right';
  public ssnInvalidErrorElement: CmElement;
  public ssnMatchErrorElement: CmElement;
  public spouseInformationSoftErrorElement: CmElement;
  public formSubmitted: boolean = false;
  public environmentName: string;

  public isFormValid: boolean;
  public formNumber: string = this.userSessionService.data.formNumber;
  public isGenericDynamicForm: boolean = this.userSessionService.data.isGenericDynamicForm;

  private cancelContent: CmHelp = this.cmService.getContentById(ContentType.HELP, "lose-data-warning");
  public modalContentHeading: string = this.cancelContent.title;
  public modalContentBody: string = FormUtils.sanitize(this.cancelContent.body);
  public cancelContentHeading: string = this.cancelContent.title;
  public cancelContentBody: string = this.cancelContent.body;
  public pageTitle: string = "About the Applicant";
  public pageInstruction: string;
  public pageInstructions: string[];

  public timeInMilliseconds: string;
  public applicantZipCodeName: string = "applicantZipCode";
  public applicantFirstNameName: string = "applicantFirstName";
  public applicantLastNameName: string = "applicantLastName";
  public applicantSsnName: string = "applicantSsn";
  public applicantBirthDateName: string = "applicantBirthDate";
  public countryOfBirthName: string = "countryOfBirth";
  public stateOfBirthName: string = "stateOfBirth";
  public countryOfResidenceName: string = "country";
  public applicantLine1Name: string = "applicantLine1";
  public applicantCityName: string = "applicantCity";
  public stateName: string = "state";
  public applicantPhoneName: string = "applicantPhone";
  public emailName: string = "email";
  public emailDefaultValue: string = "";
  public applicantBirthDateMonthName: string = "applicantBirthDateMonth";
  public applicantBirthDateDayName: string = "applicantBirthDateDay";
  public applicantBirthDateYearName: string = "applicantBirthDateYear";

  public isSsnValid: boolean = true;
  public ssnMaskLabel: string = 'Display SSN';

  public selectTextStateOfBirth: string = 'State of Birth';
  public selectTextCountryOfResidence: string = 'Where do you currently live?';
  public selectTextState: string = 'State';
  public hasSsnMatch = false;
  public isYiPolicy: boolean = false;

  public genderRadioButtonOptions = [];

  public birthplaceRadioButtonOptions = [
    { name: 'US', label: 'U.S.' },
    { name: 'NON-US', label: 'Other' }
  ];

  public communicationRadioButtonOptions = [
    { name: 'Email', label: 'Email' },
    { name: 'Postal', label: 'Mail' }
  ];

  public isGenderValid: boolean = true;
  public isBirthPlaceValid: boolean = true;
  public isCommunicationValid: boolean = true;

  public startDate: Date = new Date(Date.now());
  public taxIdMask = GlobalConstants.SOCIAL_SECURITY_PATTERN;
  public phoneMask = GlobalConstants.PHONE_MASK;
  public birthPlaceUS = GlobalConstants.BIRTH_PLACE_US;
  public birthPlaceOutsideUS = GlobalConstants.BIRTH_PLACE_OUTSIDE_THE_US;
  public zipMask = GlobalConstants.ZIP_CODE_MASK;
  public readonly CA_PROVINCES = GlobalConstants.CANADIAN_PROVINCES;
  public stateRegionList: Array<StateRegion> = GlobalConstants.US_STATES;
  public birthStateList: Array<StateRegion> = GlobalConstants.US_BIRTH_STATES;
  public countryList: Array<StateRegion> = GlobalConstants.COUNTRIES;
  public birthCountryList: Array<StateRegion> = GlobalConstants.COUNTRIES.filter(country => country.abbreviation !== 'US');
  public currentDate: Date = new Date(Date.now());
  public maxLengthName = GlobalConstants.MAX_LENGTH_NAME;
  public maxLengthLine1 = GlobalConstants.MAX_LENGTH_LINE1;
  public maxLengthCity = GlobalConstants.MAX_LENGTH_CITY;
  public maxLengthState = GlobalConstants.MAX_LENGTH_STATE;
  public maxLengthStateCd = GlobalConstants.MAX_LENGTH_STATE_CD;
  public maxLengthZip = GlobalConstants.MAX_LENGTH_ZIP;
  public maxLengthZipNonUs = GlobalConstants.MAX_LENGTH_ZIP_NON_US;
  public maxLengthEmail = GlobalConstants.MAX_LENGTH_EMAIL;
  public maxLengthPhone = GlobalConstants.PHONE_MAX_LENGTH_US;
  public readonly NAME_PATTERN = GlobalConstants.NAME_PATTERN;
  public readonly ADDRESS_PATTERN = GlobalConstants.ADDRESS_PATTERN;
  public countryOfBirthLabels  = [];
  public birthStateLabels  = [];
  public countryLabels  = [];
  public stateRegionListLabels  = [];

  public readonly today = new Date();

  public foreignAddress: boolean = false;
  public foreignBirthplace: boolean = false;

  private submitted: boolean;
  private validationInProgress: boolean = false;
  private subscriptions: Subscription[] = [];
  private timeouts: any[] = []; // Track setTimeout IDs for cleanup
  public firstNameToast: boolean = false;
  private firstNameToastShown: boolean = false;
  public dobToast: boolean = false;
  private dobToastShown: boolean = false;
  
  public continueButtonProps = {
    keyId: 'continue-button',
    size: 'large',
    text: 'Continue',
    type: 'submit',
    variant: 'primary'
  }

  genderGroup!: FormGroup; // FormGroup for the radio button group
  genderRadioButtonFormValid = true; // State to track the validity of the radio button form
  //individualRadioButtonValid = true; // State to track the validity of the individual radio button

  birthplaceGroup!: FormGroup; // FormGroup for the radio button group
  birthplaceRadioButtonFormValid = true; // State to track the validity of the radio button form

  communicationGroup!: FormGroup; // FormGroup for the radio button group
  communicationRadioButtonFormValid = true; // State to track the validity of the radio button form

  applicantInfoForm: UntypedFormGroup;

  //Display items
  firstNameElement: CmElement;
  lastNameElement: CmElement;
  dateOfBirthElement: CmElement;
  genderElement: CmElement = this.cmService.getContentById(ContentType.ELEMENT, 'gender');
  genderElementError: string = this.genderElement?.error;
  birthPlaceElement: CmElement = this.cmService.getContentById(ContentType.ELEMENT, 'birthplace');
  birthStateElement: CmElement = this.cmService.getContentById(ContentType.ELEMENT, 'birth-state');
  birthplaceHelpElement: CmTooltip;
  ssnElement: CmElement;
  actualSsn: CmElement;
  addressElement: CmElement;
  cityElement: CmElement;
  stateElement: CmElement = this.cmService.getContentById(ContentType.ELEMENT, 'state');
  zipElement: CmElement;
  countryElement: CmElement = this.cmService.getContentById(ContentType.ELEMENT, 'country-current');
  phoneElement: CmElement;
  emailElement: CmElement;
  commPrefElement: CmElement = this.cmService.getContentById(ContentType.ELEMENT, 'communication-preference');
  commPrefElementError: string = this.commPrefElement?.error;
  birthCountryElement: CmElement = this.cmService.getContentById(ContentType.ELEMENT, 'birth-country');
  birthCountryElementError: string = this.birthCountryElement?.error ?? '';
  formSection: CmSection = this.cmService.getContentById(ContentType.SECTION, "applicant-section");

  public tooltipBirthplaceKeyId: string;
  public tooltipBirthplaceHeading: string;
  public tooltipBirthplaceDescription: string;

  public actualSsnValue: string;

  applicantType :string = ''

  public labelBirthPlace: string = this.birthStateElement?.label;

  public labelForeignBirthPlace: string = this.birthCountryElement?.label;

  public labelCountry: string = this.countryElement?.label;

  public labelState: string = this.stateElement?.label;

  // GA4 Custom events
  public submitApplicantInfo = {
    action: "button_click",
    category: "applicant-info",
    name: "submit",
  }

  public cancelApplicantInfo = {
    action: "button_click",
    category: "applicant-info",
    name: "cancel",
  }

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute,
              private cmService: ManagedContentService,
              private fb: UntypedFormBuilder,
              private userSessionService: UserSessionService,
              private el: ElementRef,
              private mhsService: MhsService,
              private googleAnalyticsService: GoogleAnalyticsService,
              private authGuardService: AuthGuardService,
              private appInsightsApiService: AppInsightsApiService,
              private modalService: ModalService,
              private windowService: WindowRef,
              private appInsightsLoggerService: AppInsightsLoggerService,
              public formControlUtilitiesService: FormControlUtilitiesService,
              private cdr: ChangeDetectorRef,
              private applicationService: ApplicationService,
              private partialSaveService: PartialSaveService) {}
  private log(content: string) {
    this.appInsightsLoggerService.log('info', 'Info log from applicant-info.component', content);
  }
  get applicantFirstName() { return this.applicantInfoForm.get("applicantFirstName"); }
  get applicantLastName() { return this.applicantInfoForm.get("applicantLastName"); }
  get applicantSsn() { return this.applicantInfoForm.get("applicantSsn"); }
  get gender() { return this.applicantInfoForm.get("gender"); }
  get placeOfBirth() { return this.applicantInfoForm.get("placeOfBirth"); }
  get stateOfBirth() { return this.applicantInfoForm.get("stateOfBirth"); }
  get country() { return this.applicantInfoForm.get("country"); }
  get applicantLine1() { return this.applicantInfoForm.get("applicantLine1"); }
  get applicantCity() { return this.applicantInfoForm.get("applicantCity"); }
  get state() { return this.applicantInfoForm.get("state"); }
  get applicantZipCode() { return this.applicantInfoForm.get("applicantZipCode"); }
  get applicantPhone() { return this.applicantInfoForm.get("applicantPhone"); }
  get email() { return this.applicantInfoForm.get("email"); }
  get communicationPreference() { return this.applicantInfoForm.get("communicationPreference"); }
  get countryOfBirth() { return this.applicantInfoForm.get("countryOfBirth"); }

  ngOnInit(): void {
    if(this.userSessionService.data.sessionEnded){
      this.router.navigate(['/instructions']);
    }
    
    // Detect edit-mode entry from the review Overview "View" button.
    // Mirrors the same detection used by employee-info.component.ts so the page
    // can show Back / Save buttons and persist changes via a partial update
    // instead of the standard Continue / Cancel flow.
    const isEditFromState = history.state && history.state.editApplicant === true;
    const isEditFromQuery = this.activatedRoute.snapshot.queryParamMap.get('mode') === 'edit';
    this.isEditFromReview = isEditFromState || isEditFromQuery;
    
    // Clear the continuation flag when reaching applicant-information page (final destination)
    // But keep preservedEmployeeData so user can navigate back and still see readonly mode
    if (this.userSessionService.data.isContinuingFromConfirmation) {
      this.userSessionService.data.isContinuingFromConfirmation = false;
      // Don't clear preservedPolicyData, preservedStateData, or preservedEmployeeData
      // They will be needed if user navigates back
    }

    // Reset form submission state when navigating back to this page
    this.formSubmitted = false;
    this.submitted = false;
    
    // Restore toast shown flags from session to prevent showing again
    if (this.userSessionService.data['firstNameToastShown']) {
      this.firstNameToastShown = true;
    }
    if (this.userSessionService.data['dobToastShown']) {
      this.dobToastShown = true;
    }

    this.environmentName = environment.name;

    this.setPageTitle();
    this.pageInstruction = this.formSection?.body || '<p>Please complete the applicant information below.</p>';
    this.pageInstruction = this.pageInstruction.replace('<p>',"");
    this.pageInstruction = this.pageInstruction.replace('</p>',"");
    this.pageInstructions = this.pageInstruction.split('.');

    let today = new Date();
    this.timeInMilliseconds = today.getTime().toString();

    this.firstNameElement = this.cmService.getContentById(ContentType.ELEMENT, 'first-name');
    this.lastNameElement = this.cmService.getContentById(ContentType.ELEMENT, 'last-name');
    this.dateOfBirthElement = this.cmService.getContentById(ContentType.ELEMENT, 'date-of-birth');
    this.birthPlaceElement = this.cmService.getContentById(ContentType.ELEMENT, 'birthplace');
    this.birthStateElement = this.cmService.getContentById(ContentType.ELEMENT, 'birth-state');
    this.ssnElement = this.cmService.getContentById(ContentType.ELEMENT, 'ssn');
    this.cityElement = this.cmService.getContentById(ContentType.ELEMENT, 'city');
    this.stateElement = this.cmService.getContentById(ContentType.ELEMENT, 'state');
    this.zipElement = this.cmService.getContentById(ContentType.ELEMENT, 'zip');
    this.countryElement = this.cmService.getContentById(ContentType.ELEMENT, 'country-current');
    this.phoneElement = this.cmService.getContentById(ContentType.ELEMENT, 'phone');
    this.emailElement = this.cmService.getContentById(ContentType.ELEMENT, 'email');

    this.ssnInvalidErrorElement = this.cmService.getContentById(ContentType.ELEMENT, 'ssnInvalidError');
    this.ssnMatchErrorElement = this.cmService.getContentById(ContentType.ELEMENT, 'ssnMatchError');
    this.spouseInformationSoftErrorElement = this.cmService.getContentById(ContentType.ELEMENT, 'spouse-information-soft-error');

    if(this.userSessionService?.data?.mhsApplication?.econsent?.email != undefined){
      this.emailDefaultValue = this.userSessionService.data.mhsApplication.econsent.email;
    }

    if(this.userSessionService.data.applicantType){
      this.applicantType = this.userSessionService.data.applicantType?.toLowerCase();
    }

    if(this.userSessionService.data.companyId === COMPANY_ID.YI ||
      this.isGenericDynamicForm){
      this.isYiPolicy = true;
      this.genderRadioButtonOptions = [
        { name: 'male', label: 'Male' },
        { name: 'female', label: 'Female' },
        { name: 'non-binary', label: 'Non-Binary' }
      ];
    }else if(this.userSessionService.data.companyId === COMPANY_ID.NM && this.userSessionService.data.state === "NY"){
      this.isYiPolicy = false;
      this.genderRadioButtonOptions = [
        { name: 'male', label: 'Male' },
        { name: 'female', label: 'Female' },
        { name: 'non-binary', label: 'Non-Binary' }
      ];
    } else {
      this.isYiPolicy = false;
      this.genderRadioButtonOptions = [
        { name: 'male', label: 'Male' },
        { name: 'female', label: 'Female' }
      ];
    }

    this.birthplaceHelpElement = this.cmService.getContentById(ContentType.TOOLTIP, "birthplace");
    if(this.birthplaceHelpElement){
      this.tooltipBirthplaceKeyId = 'helpBirthplace';
      this.tooltipBirthplaceHeading = this.birthplaceHelpElement.title;
      this.tooltipBirthplaceDescription = this.birthplaceHelpElement.plainText;
    }

    // Check if form already exists in parent (user navigated back to this page)
    const savedApplicantData = this.userSessionService.data['applicantFormData'];
    
    if (this.parentForm && this.parentForm.contains('applicant')) {
      // Use existing form to preserve data
      this.applicantInfoForm = this.parentForm.get('applicant') as UntypedFormGroup;
    } else {
      // Create new form for first visit
      this.applicantInfoForm = this.fb.group({
        applicantFirstName: ['', [Validators.required, Validators.maxLength(GlobalConstants.MAX_LENGTH_NAME), Validators.pattern(this.NAME_PATTERN)]],
        applicantLastName: ['', [Validators.required, Validators.maxLength(GlobalConstants.MAX_LENGTH_NAME), Validators.pattern(this.NAME_PATTERN)]],
        applicantSsn: ['', [Validators.required, Validators.pattern(GlobalConstants.SSN_VALIDATOR_PATTERN)]],
        applicantBirthDateMonth: ['', []],
        applicantBirthDateDay: ['', []],
        applicantBirthDateYear: ['', []],
        gender: ['', [Validators.required]],
        placeOfBirth: [GlobalConstants.BIRTH_PLACE_US, [Validators.required]],
        stateOfBirth: ['', [Validators.required]],
        country: ["US", [ Validators.maxLength(50), Validators.required]],
        applicantLine1: ['', [Validators.pattern(this.ADDRESS_PATTERN),Validators.maxLength(this.maxLengthLine1), Validators.required]],
        applicantCity: ['', [ Validators.maxLength(this.maxLengthCity), Validators.required, Validators.pattern(this.NAME_PATTERN)]],
        state: ['', [ Validators.maxLength(this.maxLengthStateCd), Validators.required]],
        applicantZipCode: ['', [ Validators.pattern(GlobalConstants.POSTAL_CODE_PATTERN_US), Validators.required]],
        applicantPhone: ['', [ Validators.pattern(GlobalConstants.PHONE_REGEX), Validators.required]],
        email: ['', 
                {
                      validators: [
                        Validators.required,
                        Validators.pattern(GlobalConstants.EMAIL_PATTERN),
                        Validators.maxLength(255)
                      ],
                      asyncValidators: [this.validateEmailAddress()],
                      updateOn: 'blur' 
                    }
              ],
        communicationPreference: ['', [Validators.required]],
        toggleSsnMask:[''],
        actualSsn: null,
      });
      
      

      // Restore saved data if it exists
      if (savedApplicantData) {
        this.applicantInfoForm.patchValue(savedApplicantData, { emitEvent: false });
        // Mark form as untouched and pristine to prevent validation errors from showing
        this.applicantInfoForm.markAsUntouched();
        this.applicantInfoForm.markAsPristine();
      }
    }
     
    if(this.userSessionService.data.applicantType){
      if (this.userSessionService.data.applicantType?.toLowerCase() != "employee") {
        this.applicantInfoForm.get("applicantSsn").addValidators(this.ssnMatchesEmployeeValidator());
      } else {
        // For employee applicantType, clear validators and copy employee info
        this.copyEmployeeInfo();
      }
    }

    this.foreignBirthplace = false;

    this.countryOfBirthLabels = [this.labelForeignBirthPlace].concat(this.birthCountryList.map(birthCountry => birthCountry.label));
    this.birthStateLabels = [this.selectTextStateOfBirth].concat(this.birthStateList.map(state => state.label));
    this.countryLabels = [this.selectTextCountryOfResidence].concat(this.countryList.map(country => country.label));
    this.stateRegionListLabels = [this.selectTextState].concat(this.stateRegionList.map(stateRegion => stateRegion.label));

    // Add form to parent if it was newly created
    const wasNewlyCreated = this.parentForm && !this.parentForm.contains('applicant');
    if (wasNewlyCreated) {
      this.parentForm.addControl('applicant', this.applicantInfoForm);
    }

    // Restore saved data AFTER adding to parent (only if we just created a new form)
    if (savedApplicantData && wasNewlyCreated) {
      // Set flag to prevent state/zip from being cleared during restoration
      this.validationInProgress = true;
      
      // Fields to skip for employee applicant type (already populated by copyEmployeeInfo)
      const employeeSkipFields = ['applicantFirstName', 'applicantLastName', 'applicantSsn', 
                                   'applicantBirthDateMonth', 'applicantBirthDateDay', 'applicantBirthDateYear'];
      const isEmployee = this.userSessionService.data.applicantType?.toLowerCase() === 'employee';
      
      // Restore actualSsnValue from session if it exists
      if (savedApplicantData['actualSsn'] && !isEmployee) {
        this.actualSsnValue = savedApplicantData['actualSsn'];
      }
      
      // For prism-ui components, we need to set values immediately before the view renders
      // Set values synchronously first
      Object.keys(savedApplicantData).forEach(key => {
        // Skip employee-specific fields if applicantType is 'employee'
        if (isEmployee && employeeSkipFields.includes(key)) {
          return;
        }
        
        // Special handling for applicantSsn: always use actualSsn if available (to avoid masked values)
        if (key === 'applicantSsn') {
          const ssnValue = savedApplicantData['actualSsn'] || savedApplicantData[key];
          const control = this.applicantInfoForm.get(key);
          if (control && ssnValue !== null && ssnValue !== undefined) {
            control.setValue(ssnValue, { emitEvent: false });
            control.markAsUntouched();
            control.markAsPristine();
          }
          return;
        }
        
        const control = this.applicantInfoForm.get(key);
        if (control && savedApplicantData[key] !== null && savedApplicantData[key] !== undefined) {
          control.setValue(savedApplicantData[key], { emitEvent: false });
          // Mark as untouched and pristine immediately to prevent validation errors
          control.markAsUntouched();
          control.markAsPristine();
        }
      });
      
      // Recalculate foreignAddress flag based on restored country value
      const savedCountry = savedApplicantData['country'];
      if (savedCountry) {
        this.countryOfResidenceChange(savedCountry);
      }
      
      // Recalculate foreignBirthplace flag based on restored placeOfBirth value
      const savedPlaceOfBirth = savedApplicantData['placeOfBirth'];
      if (savedPlaceOfBirth) {
        this.toggleBirthDropDown(savedPlaceOfBirth);
      }
      
      // Then schedule a second update with events to trigger web component updates
      const timeoutId1 = setTimeout(() => {
        Object.keys(savedApplicantData).forEach(key => {
          // Skip employee-specific fields if applicantType is 'employee'
          if (isEmployee && employeeSkipFields.includes(key)) {
            return;
          }
          
          // Special handling for applicantSsn: always use actualSsn if available (to avoid masked values)
          if (key === 'applicantSsn') {
            const ssnValue = savedApplicantData['actualSsn'] || savedApplicantData[key];
            const control = this.applicantInfoForm.get(key);
            if (control && ssnValue !== null && ssnValue !== undefined) {
              control.setValue(ssnValue, { emitEvent: true });
              control.markAsUntouched();
              control.markAsPristine();
              control.updateValueAndValidity();
            }
            return;
          }
          
          const control = this.applicantInfoForm.get(key);
          if (control && savedApplicantData[key] !== null && savedApplicantData[key] !== undefined) {
            control.setValue(savedApplicantData[key], { emitEvent: true });
            // Mark as untouched and pristine to prevent validation errors from showing
            control.markAsUntouched();
            control.markAsPristine();
            control.updateValueAndValidity();
          }
        });
        this.cdr.detectChanges();
        // Reset validation flag after restoration is complete
        this.validationInProgress = false;
        
        // Force web components to update by directly manipulating them
        const timeoutId2 = setTimeout(() => {
          this.forceWebComponentUpdate();
        }, 50);
        this.timeouts.push(timeoutId2);
      }, 100);
      this.timeouts.push(timeoutId1);
    }

    if(this.mhsService){
      const sub1 = this.mhsService.submitComponent
        .pipe(
          // BehaviorSubject replays its latest value on subscribe; skip it to
          // avoid auto-submitting when a stale `true` is retained.
          skip(1),
          filter(msg => msg === true)
        )
        .subscribe(() => {
          this.onSubmit();
        });
      this.subscriptions.push(sub1);
    }

    if(this.mhsService){
      const sub2 = this.mhsService.submitApplicantInfo
        .pipe(
          // BehaviorSubject replays its latest value on subscribe; skip it to
          // avoid auto-submitting when a stale `true` is retained.
          skip(1),
          filter(msg => msg === true)
        )
        .subscribe(() => {
          this.onSubmit();
        });
      this.subscriptions.push(sub2);
    }

    if(this.windowService){
      this.windowService.nativeDocument
        .querySelector('body')
        .addEventListener('click', (event: Event) => this.onClick(event));
    }

    this.disableAutoFill();

    this.resizeObservable$ = fromEvent(window, 'resize')
    this.resizeSubscription$ = this.resizeObservable$.subscribe( evt => {
      this.innerWidth = window.innerWidth;
      this.changeTooltipPosition();
      this.loadManagedContent();
    });

    this.changeTooltipPosition();
    this.loadManagedContent();
  }//end ngOnInit

  ngAfterViewInit() {
    this.appInsightsApiService.endNavigationEvent(this.router.url);
    this.applicantInfoForm.get('toggleSsnMask').setValue(true);

    if(this.emailDefaultValue != ''){
      this.applicantInfoForm.get('email').setValue(this.emailDefaultValue);
      this.applicantInfoForm.get('email').markAsTouched();
      this.applicantInfoForm.get('email').updateValueAndValidity();
    }
    
    this.innerWidth = window.innerWidth;
    this.changeTooltipPosition();
    this.loadManagedContent();
  }

  ngOnDestroy() {
    // Clear all pending timeouts
    this.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.timeouts = [];
    
    // Unsubscribe from all subscriptions to prevent memory leaks and duplicate calls
    this.subscriptions.forEach(sub => sub.unsubscribe());
    
    // Unsubscribe from resize subscription
    if (this.resizeSubscription$) {
      this.resizeSubscription$.unsubscribe();
    }
    
    // Save form data to session before component is destroyed.
    // Skip if the user clicked Back without saving (discardFormDataOnDestroy) to
    // prevent overwriting the session cache with unsaved/empty form data.
    if (this.applicantInfoForm && !this.discardFormDataOnDestroy) {
      const formValue = this.applicantInfoForm.value;
      const hasData = Object.values(formValue).some(value => 
        value !== null && value !== undefined && value !== ''
      );
      
      if (hasData) {
        this.userSessionService.data['applicantFormData'] = formValue;
      }
    }
  }

  private forceWebComponentUpdate() {
    // Handle all prism-ui input component types
    const componentSelectors = [
      'prism-ui-input-text',
      'prism-ui-input-number',
      'prism-ui-input-currency',
      'prism-ui-input-ssn',
      'prism-ui-input-tel',
      'prism-ui-input-phone',
      'prism-ui-input-email'
    ];

    componentSelectors.forEach(selector => {
      const inputs = document.querySelectorAll(selector);
      inputs.forEach((input: any) => {
        const formControlName = input.getAttribute('formControlName');
        if (formControlName) {
          // For SSN field, use actualSsn if applicantSsn is empty
          let controlValue: any;
          if (formControlName === 'applicantSsn') {
            const ssnControl = this.applicantInfoForm.get('applicantSsn');
            const actualSsnControl = this.applicantInfoForm.get('actualSsn');
            controlValue = (ssnControl?.value || actualSsnControl?.value || this.actualSsnValue);
          } else {
            const control = this.applicantInfoForm.get(formControlName);
            controlValue = control?.value;
          }
          
          if (controlValue !== null && controlValue !== undefined && controlValue !== '') {
            // Try to set the value on the web component's shadow DOM
            if (input.shadowRoot) {
              const innerInput = input.shadowRoot.querySelector('input');
              if (innerInput) {
                innerInput.value = controlValue;
                // Dispatch input event to trigger any listeners
                innerInput.dispatchEvent(new Event('input', { bubbles: true }));
              }
            }
            
            // Set the defaultValue property
            input.defaultValue = controlValue;
            
            // Also try setting the value property directly
            if (input.value !== controlValue) {
              input.value = controlValue;
            }
            
            // For SSN and other special components, set the default-value attribute
            if (selector === 'prism-ui-input-ssn' || selector === 'prism-ui-input-phone' || selector === 'prism-ui-input-email') {
              input.setAttribute('default-value', controlValue);
            }
          }
        }
      });
    });
  }


  changeTooltipPosition(){
    if(this.innerWidth < 450){
      this.birthplaceTooltipPosition = 'left';
    } else {
      this.birthplaceTooltipPosition = 'right';
    }
  }

  loadManagedContent() {
    if(this.innerWidth <= 425){
      this.addressElement = this.cmService.getContentById(ContentType.ELEMENT, 'applicant-address-sm');
    } else {
      this.addressElement = this.cmService.getContentById(ContentType.ELEMENT, 'applicant-address');
    }

    if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.applicantInfoForm, 'country')){
      if(this.applicantInfoForm.get("country").value != 'US'){
        if(this.innerWidth <= 425){
          this.stateElement = this.cmService.getContentById(ContentType.ELEMENT, 'state-province-sm');
        } else {
          this.stateElement = this.cmService.getContentById(ContentType.ELEMENT, 'state-province');
        }
      }else {
        this.stateElement = this.cmService.getContentById(ContentType.ELEMENT, 'state');
      }
    }
  }

  public setPageTitle(){
    if(this.userSessionService.data.applicantType){
      const at = this.userSessionService.data.applicantType?.toLowerCase();
      if(at === "employee"){
        this.pageTitle = "About the Applicant (Employee)";
      } else if(at === ApplicantType.SPOUSE.toLowerCase()){
        this.pageTitle = "About the Applicant (Spouse)";
      } else if(at === ApplicantType.CHILD.toLowerCase()){
        this.pageTitle = "About the Applicant (Child)";
      }
    }
  }

  private disableAutoFill(){

    // Add autocomplete='off' to form elements
    Array.from(document.querySelectorAll('form'))
    .forEach(element => {
      element.setAttribute('autocomplete', 'off');
    });

    this.applicantZipCodeName = this.timeInMilliseconds + "-" + this.applicantZipCodeName;
    this.applicantFirstNameName = this.timeInMilliseconds + "-" + this.applicantFirstNameName;
    this.applicantLastNameName = this.timeInMilliseconds + "-" + this.applicantLastNameName;
    this.applicantSsnName = this.timeInMilliseconds + "-" + this.applicantSsnName;
    this.applicantBirthDateName = this.timeInMilliseconds + "-" + this.applicantBirthDateName;
    this.applicantLine1Name = this.timeInMilliseconds + "-" + this.applicantLine1Name;
    this.applicantCityName = this.timeInMilliseconds + "-" + this.applicantCityName;
    this.stateName = this.timeInMilliseconds + "-" + this.stateName;
    this.emailName = this.timeInMilliseconds + "-" + this.emailName;
    this.applicantPhoneName = this.timeInMilliseconds + "_" + this.applicantPhoneName;
    this.applicantBirthDateMonthName = this.timeInMilliseconds + "-" + this.applicantBirthDateMonthName;
    this.applicantBirthDateDayName = this.timeInMilliseconds + "-" + this.applicantBirthDateDayName;
    this.applicantBirthDateYearName = this.timeInMilliseconds + "-" + this.applicantBirthDateYearName;
  }

  // Event Handlers
  handleOptionSelectedStateOfBirth(event: Event, key: string) {

    const customEvent = event as CustomEvent;
    let optSelected = customEvent.detail;
    let optValue = this.getStateOfBirthValue(optSelected);
    this.applicantInfoForm.get(key).setValue(optValue);
  }

  getStateOfBirthValue(optSelected: string){
    for(let state of this.birthStateList){
      if(state.label === optSelected){
        return state.abbreviation;
      }
    }

    if(optSelected != null && optSelected != undefined && optSelected != ""){
      return this.birthStateList[0].abbreviation;
    } else {
      return null;
    }
  }

  getStateOfBirthLabel(optSelected: string){
    for(let state of this.birthStateList){
      if(state.abbreviation === optSelected){
        return state.label;
      }
    }
    return null;
  }

  handleOptionSelectedCountryOfBirth(event: Event, key: string) {

    const customEvent = event as CustomEvent;
    let optSelected = customEvent.detail;
    let optValue = this.getCountryOfBirthValue(optSelected);
    this.applicantInfoForm.get(key).setValue(optValue);
  }

  getCountryOfBirthValue(optSelected: string){
    for(let country of this.birthCountryList){
      if(country.label === optSelected){
        return country.abbreviation;
      }
    }

    if(optSelected != null && optSelected != undefined && optSelected != ""){
      return this.birthCountryList[0].abbreviation;
    } else {
      return null;
    }
  }

  getCountryOfBirthLabel(optSelected: string){
    for(let country of this.birthCountryList){
      if(country.abbreviation === optSelected){
        return country.label;
      }
    }
    return null;
  }

  handleOptionSelectedState(event: Event, key: string) {

    const customEvent = event as CustomEvent;
    let optSelected = customEvent.detail;
    let optValue = this.getStateValue(optSelected);
    this.applicantInfoForm.get(key).setValue(optValue);
  }

  getStateValue(optSelected: string){
    for(let state of this.stateRegionList){
      if(state.label === optSelected){
        return state.abbreviation;
      }
    }

    if(optSelected != null && optSelected != undefined && optSelected != ""){
      return this.stateRegionList[0].abbreviation;
    } else {
      return null;
    }
  }

  getStateLabel(optSelected: string){
    for(let state of this.stateRegionList){
      if(state.abbreviation === optSelected){
        return state.label;
      }
    }
    return null;
  }

  handleOptionSelectedCountryOfResidence(event: Event, key: string) {

    this.applicantInfoForm.get("country").markAsTouched();

    const customEvent = event as CustomEvent;
    let optSelected = customEvent.detail;
    let optValue = this.getCountryofResidenceValue(optSelected);
    this.applicantInfoForm.get(key).setValue(optValue);

    this.countryOfResidenceChange(optValue);

    this.applicantInfoForm.get("country").updateValueAndValidity();
  }

  getCountryofResidenceValue(optSelected: string){
    for(let country of this.countryList){
      if(country.label === optSelected){
        return country.abbreviation;
      }
    }

    if(optSelected != null && optSelected != undefined && optSelected != ""){
      return this.countryList[0].abbreviation;
    } else {
      return null;
    }
  }

  getCountryofResidenceLabel(optSelected: string){
    for(let country of this.countryList){
      if(country.abbreviation === optSelected){
        return country.label;
      }
    }
    return null;
  }

  toggleBirthDropDown(place: string){

    if(place === "US"){

      this.foreignBirthplace = false;

      //remove foreign control
      this.applicantInfoForm.removeControl('countryOfBirth');

      //add US control
      this.applicantInfoForm.addControl('stateOfBirth', this.fb.control('', [Validators.required]));

    } else {

      this.foreignBirthplace = true;

      //remove US control
      this.applicantInfoForm.removeControl('stateOfBirth');

      //add foreign control
      this.applicantInfoForm.addControl('countryOfBirth', this.fb.control('', [Validators.required, ]));

    }
  }

  copyEmployeeInfo(): void {
    let employee = new EmployeeMhs();
    if(this.userSessionService.data.mhsApplication.employee != undefined){

    employee = this.userSessionService.data.mhsApplication.employee;

    this.applicantFirstName.setValue(employee.firstName);
    this.applicantFirstName.clearValidators();
    this.applicantFirstName.updateValueAndValidity();

    this.applicantLastName.setValue(employee.lastName);
    this.applicantLastName.clearValidators();
    this.applicantLastName.updateValueAndValidity();

    let birthDateDay = employee.birthDate.split('-');
    this.applicantInfoForm.get('applicantBirthDateYear').setValue(birthDateDay[0]);
    this.applicantInfoForm.get('applicantBirthDateYear').clearValidators();
    this.applicantInfoForm.get('applicantBirthDateYear').updateValueAndValidity();

    this.applicantInfoForm.get('applicantBirthDateMonth').setValue(birthDateDay[1]);
    this.applicantInfoForm.get('applicantBirthDateMonth').clearValidators();
    this.applicantInfoForm.get('applicantBirthDateMonth').updateValueAndValidity();

    this.applicantInfoForm.get('applicantBirthDateDay').setValue(birthDateDay[2]);
    this.applicantInfoForm.get('applicantBirthDateDay').clearValidators();
    this.applicantInfoForm.get('applicantBirthDateDay').updateValueAndValidity();

    this.applicantSsn.setValue(employee.ssn);
    this.applicantSsn.clearValidators();
    this.applicantSsn.updateValueAndValidity();
    }
  }

  //trim beginning and ending whitespace
  firstNameLeaveEvent(event){
    const inputValue = event.target.value;
    this.applicantFirstName.setValue(inputValue.toString().trim());
    this.applicantFirstName.markAsTouched();
    
    // Display soft message: toast
    if (
      this.userSessionService.data.applicantType?.toLowerCase() ===
        ApplicantType.SPOUSE.toLowerCase() &&
      this.userSessionService.data.mhsApplication.employee.firstName?.toLowerCase() ==
        inputValue?.toLowerCase()
    ) {
      // Only show toast if it hasn't been shown before (check session storage)
      if (!this.firstNameToastShown && !this.userSessionService.data['firstNameToastShown']) {
        this.firstNameToast = true;
        this.firstNameToastShown = true;
        // Persist to session so it won't show again on navigation
        this.userSessionService.data['firstNameToastShown'] = true;
      }
    } else {
      // Reset toast flags if condition is no longer met
      this.firstNameToast = false;
      this.firstNameToastShown = false;
      this.userSessionService.data['firstNameToastShown'] = false;
    }
  }

  //trim beginning and ending whitespace
  lastNameLeaveEvent(event){
    const inputValue = event.target.value;
    this.applicantLastName.setValue(inputValue.toString().trim());
    this.applicantLastName.markAsTouched();
  }

  //trim beginning and ending whitespace
  AddressLine1LeaveEvent(event){
    const inputValue = event.target.value;
    this.applicantInfoForm.get('applicantLine1').markAsTouched();
    this.applicantInfoForm.controls["applicantLine1"].setValue(inputValue.toString().trim());
    this.applicantInfoForm.updateValueAndValidity();
  }

  //trim beginning and ending whitespace
  CityLeaveEvent(event){
    const inputValue = event.target.value;
    this.applicantInfoForm.get('applicantCity').markAsTouched();
    this.applicantInfoForm.controls["applicantCity"].setValue(inputValue.toString().trim());
    this.applicantInfoForm.updateValueAndValidity();
  }

  //trim beginning and ending whitespace
  stateLeaveEvent(event){
    const inputValue = event.target.value;
    this.applicantInfoForm.get('state').markAsTouched();
    this.applicantInfoForm.controls["state"].setValue(inputValue.toString().trim());
    this.applicantInfoForm.updateValueAndValidity();
  }

  //trim beginning and ending whitespace
  countryOfBirthLeaveEvent(event){
    const inputValue = event.target.value;
    this.applicantInfoForm.controls["countryOfBirth"].setValue(inputValue.toString().trim());
  }

  //trim beginning and ending whitespace
  applicantZipCodeLeaveEvent(event){
    const inputValue = event.target.value;
    this.applicantInfoForm.get('applicantZipCode').markAsTouched();
    this.applicantInfoForm.controls["applicantZipCode"].setValue(inputValue.toString().trim());
    this.applicantInfoForm.updateValueAndValidity();
  }

  //trim beginning and ending whitespace
  phoneLeaveEvent(event){
    const inputValue = event.target.value;
    this.applicantInfoForm.get('applicantPhone').markAsTouched();
    this.applicantInfoForm.controls["applicantPhone"].setValue(inputValue.toString().replace(/\D/g, ''));
    this.applicantInfoForm.updateValueAndValidity();
  }

  //trim beginning and ending whitespace
  emailLeaveEvent(event){
    const inputValue = event.target.value;
    this.applicantInfoForm.get('email').markAsTouched();
    this.applicantInfoForm.controls["email"].setValue(inputValue.toString().trim());
    this.applicantInfoForm.updateValueAndValidity();
  }

  changeCountry(event){

    if(event.value === 'US' &&
       this.applicantInfoForm.get('applicantZipCode').value != null &&
       this.applicantInfoForm.get('applicantZipCode').value != "" &&
       this.applicantInfoForm.get('applicantZipCode').value != undefined){

      this.applicantInfoForm.get('applicantZipCode').setValue(null);
    }

  }

  countryOfResidenceChange(value){

    // Clear state value on country change
    if(!this.validationInProgress){
      this.applicantInfoForm.get("state").setValue("");
      this.applicantInfoForm.get("applicantZipCode").setValue("");
    }

    // FOREIGN ADDRESS
    // If country code is not empty and not US, adjust for foreign address
    // remove required validator, swap cm content, set foreignAddress flag for ui
    if(value != null && value != '' && value != 'US'){
      this.foreignAddress = true;

      if(this.innerWidth <= 425){
        this.stateElement = this.cmService.getContentById(ContentType.ELEMENT, 'state-province-sm');
      } else {
        this.stateElement = this.cmService.getContentById(ContentType.ELEMENT, 'state-province');
      }
      
      this.zipElement = this.cmService.getContentById(ContentType.ELEMENT, 'zip-postal');
      this.zipMask = null;
      this.phoneMask = GlobalConstants.PHONE_MASK_NON_US;
      this.maxLengthPhone = GlobalConstants.PHONE_MAX_LENGTH_NON_US;

      //clear the validators
      this.applicantInfoForm.get('applicantCity').clearValidators();
      this.applicantInfoForm.get('state').clearValidators();
      this.applicantInfoForm.get('applicantZipCode').clearValidators();
      this.applicantInfoForm.get('applicantPhone').clearValidators();

      // Set new validators for city, state, zip
      this.applicantInfoForm.get('applicantCity').setValidators([
        Validators.required,
        Validators.maxLength(this.maxLengthCity),
        Validators.pattern(this.NAME_PATTERN)
      ]);
      this.applicantInfoForm.get('state').setValidators([
        Validators.required,
        Validators.maxLength(this.maxLengthState),
        Validators.pattern("^(.*[^ ].*)$")
      ]);
      this.applicantInfoForm.get('applicantZipCode').setValidators([
        Validators.required,
        Validators.maxLength(this.maxLengthZipNonUs),
        Validators.pattern(GlobalConstants.POSTAL_CODE_PATTERN_NON_US)
      ]);
      this.setApplicantPhoneValidators();
    } else {
      // US ADDRESS
      this.foreignAddress = false;
      this.stateElement = this.cmService.getContentById(ContentType.ELEMENT, 'state');
      this.zipElement = this.cmService.getContentById(ContentType.ELEMENT, 'zip');
      this.zipMask = GlobalConstants.ZIP_CODE_MASK;
      this.phoneMask = GlobalConstants.PHONE_MASK;
      this.maxLengthPhone = GlobalConstants.PHONE_MAX_LENGTH_US;

      this.applicantInfoForm.get('applicantCity').setValidators([
        Validators.required,
        Validators.maxLength(this.maxLengthCity),
        Validators.pattern(this.NAME_PATTERN)
      ]);
      this.applicantInfoForm.get('state').setValidators([
        Validators.required,
        Validators.maxLength(this.maxLengthStateCd),
        Validators.pattern("^(.*[^ ].*)$")
      ]);
      this.applicantInfoForm.get('applicantZipCode').setValidators([
        Validators.required,
        Validators.pattern(GlobalConstants.POSTAL_CODE_PATTERN_US)
      ]);
      this.setApplicantPhoneValidators();

    }

    // Update value and validity
    this.applicantInfoForm.get('applicantCity').updateValueAndValidity();
    this.applicantInfoForm.get('state').updateValueAndValidity();
    this.applicantInfoForm.get('applicantZipCode').updateValueAndValidity();
    this.applicantInfoForm.get('applicantPhone').updateValueAndValidity();

    // Clear state if an invalid value remains
    if(!this.applicantInfoForm.get('state').valid){
      this.applicantInfoForm.get('state').setValue("");
    }
  }

  private setApplicantPhoneValidators() {
    if (this.foreignAddress) {
      this.applicantInfoForm.get('applicantPhone').setValidators([
        Validators.required,
        Validators.maxLength(this.maxLengthPhone),
        Validators.pattern("^(.*[^ ].*)$")
      ]);
    } else {
      this.applicantInfoForm.get('applicantPhone').setValidators([
        Validators.required,
        Validators.pattern(GlobalConstants.PHONE_REGEX)
      ]);
    }
  }

  /*this code fixes a weird bug.  If the user fills in all of the info, and then switches the
  country where they currently live to a foreign country, and then they decrease the phone number,
  the phone field will throw a validation error.  But the weird thing is, neither of the two
  validators on the phone field (the 2 if statemenets below) are triggering the error.  And those
  are the only 2 validators on the field.  So it's a false positive.  The code in the else statement is
  kind of weird, but it works.  I removed the validators and put them back in place, and the field no longer
  turns red for no reason.  The validators are still in place, so the user has to enter numbers and can't
  go over 10 digits.
  */
  phoneLeave(){
    this.applicantPhone.markAsTouched();
    this.applicantInfoForm.get('applicantPhone').clearValidators();
    this.applicantInfoForm.get('applicantPhone').setErrors(null);
    this.applicantInfoForm.get('applicantPhone').updateValueAndValidity();
    this.setApplicantPhoneValidators();
    this.applicantInfoForm.get('applicantPhone').updateValueAndValidity();
  }

  onSubmit(){
    this.formSubmitted = true;
    const spinnerStartTime = new Date().getTime();

    this.appInsightsLoggerService.log('info', 'ApplicantInfoComponent: Spinner Started.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');

    //always check the ssn value before submitting because we have had issues with the ssn value
    this.applicantInfoForm.get('applicantSsn').markAsTouched();
    this.applicantInfoForm.get('applicantSsn').updateValueAndValidity();
    
    //always check the ssn value before submitting because we have had issues with the ssn value
    if(this.userSessionService.data.applicantType?.toLowerCase() !== "employee"){
      this.formControlUtilitiesService.ssnLeaveEvent(this.applicantInfoForm , 'applicantSsn', this.hasSsnMatch);
    }

    this.touchAllControls();

    this.googleAnalyticsService.sendCustomEvent(this.submitApplicantInfo);
    this.applicantInfoForm.markAllAsTouched();
    this.applicantInfoForm.updateValueAndValidity();
    
    //check the ssn value after marking all as touched
  if(this.userSessionService.data.applicantType?.toLowerCase() !== "employee"){
      this.formControlUtilitiesService.ssnLeaveEvent(this.applicantInfoForm , 'applicantSsn', this.hasSsnMatch);
    }
    
    this.validateRadioButtons();
    if(this.userSessionService.data.applicantType?.toLowerCase() === "employee"){
      if(this.userSessionService.data.mhsApplication.employee != undefined){
        this.copyEmployeeInfo();
      } else {
        let employee: EmployeeMhs;
        employee = this.userSessionService.data.mhsApplication.employee;

        this.applicantInfoForm.get("applicantFirstName").setValue(employee.firstName);
        this.applicantInfoForm.get("applicantLastName").setValue(employee.lastName);
        this.applicantInfoForm.get("applicantSsn").setValue(employee.ssn);
        let birthDateDay = employee.birthDate.split('-')[2];
        this.applicantInfoForm.get("applicantBirthDateYear").setValue(birthDateDay[0]);
        this.applicantInfoForm.get("applicantBirthDateMonth").setValue(birthDateDay[1]);
        this.applicantInfoForm.get("applicantBirthDateDay").setValue(birthDateDay[2]);
        this.applicantInfoForm.updateValueAndValidity();
      }
    }

    if(this.applicantInfoForm.valid){
      if(this.environmentName === 'demonstration'){
        this.swapSSN();
        this.fillInApplicationInfo();
        this.submitted = true;
        this.val.emit(this.submitted);
        
      } else {
        this.swapSSN();
        this.fillInApplicationInfo();
        this.savePartialApplication(spinnerStartTime);
      }
      
    } else {

      this.scrollToFirstInvalidControl();
      this.submitted = false;
      this.formSubmitted = false;
      this.appInsightsLoggerService.log('info', 'ApplicantInfoComponent: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      const spinnerStopTime = new Date().getTime();
      const spinnerDuration = spinnerStopTime - spinnerStartTime;
      this.appInsightsLoggerService.log('info', 'ApplicantInfoComponent: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
    }

  }

  async savePartialApplication(spinnerStartTime: number){
    let resp = new PartialSaveResp();
    try {
      const result = await this.applicationService.updateApplicantWithExStreamCloudApplication(this.userSessionService.data.mhsId, this.userSessionService.data.mhsApplication.applicant);
      resp = result;
      this.appInsightsLoggerService.log('info', 'Partial application saved successfully.  Component: ' + 'ApplicantInfoComponent' + ' mhsId/caseId: ' + result.data.mhsId, '');
      
      // 201 (Created) and 304 (Not Modified) are both successful responses
      if(resp.httpStatusCode === 201 || resp.httpStatusCode === 304){

        this.setEConsent();

        this.submitted = true;
        
        // In edit mode, navigate back to review instead of advancing to next step
        if (this.isEditFromReview) {
          this.isSavingEdit = false;
          this.formSubmitted = false;
          this.cdr.detectChanges();
          this.navigateBackToReviewAfterSave();
        } else {
          //advance to the next step only after partial save is successful
          this.val.emit(this.submitted);
          this.formSubmitted = false;
        }
        
        this.appInsightsLoggerService.log('info', 'ApplicantInfoComponent: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        const spinnerStopTime = new Date().getTime();
        const spinnerDuration = spinnerStopTime - spinnerStartTime;
        this.appInsightsLoggerService.log('info', 'ApplicantInfoComponent: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      } else {
        this.appInsightsLoggerService.log('error', 'Applicant updated successfully. mhsId/caseId: ' + resp.data.mhsId, '');
        this.submitted = false;
        this.formSubmitted = false;
        this.isSavingEdit = false;
        this.cdr.detectChanges();
        this.appInsightsLoggerService.log('info', 'ApplicantInfoComponent: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        const spinnerStopTime = new Date().getTime();
        const spinnerDuration = spinnerStopTime - spinnerStartTime;
        this.appInsightsLoggerService.log('info', 'ApplicantInfoComponent: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      }
    } catch (error) {
      let resp = new PartialSaveResp();
      resp.httpStatusCode = error.error.httpStatusCode;
      resp.message = error.message;
      resp.data = error.error.data;
      this.findInvalidControls(resp.data);
      this.appInsightsLoggerService.log('error', 'Applicant update failed. mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      this.submitted = false;
      this.formSubmitted = false;
      this.isSavingEdit = false;
      this.cdr.detectChanges();
      this.appInsightsLoggerService.log('info', 'ApplicantInfoComponent: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      const spinnerStopTime = new Date().getTime();
      const spinnerDuration = spinnerStopTime - spinnerStartTime;
      this.appInsightsLoggerService.log('info', 'ApplicantInfoComponent: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
    }
  }

  findInvalidControls(resp: PartialSaveData){
    for(const key in resp){
      if(key === "applicant.address.line1"){
        this.applicantInfoForm.get('applicantLine1').setErrors({ pattern: resp[key] });
      } else if(key === "applicant.address.city"){
        this.applicantInfoForm.get('applicantCity').setErrors({ pattern: resp[key] });
      } else if(key === "applicant.address.zip"){
        this.applicantInfoForm.get('applicantZipCode').setErrors({ pattern: resp[key] });
      } else if(key === "applicant.phone"){
        this.applicantInfoForm.get('applicantPhone').setErrors({ pattern: resp[key] });
      } else if(key === "applicant.email"){
        this.applicantInfoForm.get('email').setErrors({ pattern: resp[key] });
      }
    }

    this.scrollToFirstInvalidControl();
  }

  scrollToFirstInvalidControl(){
    var firstInvalidControlName: string = FormUtils.findFirstInvalidControl(this.applicantInfoForm);
    const firstInvalidControl = document.querySelector(`[formControlName="${firstInvalidControlName}"]`);
    var headerOffset = 170;
    var elementPosition = firstInvalidControl.getBoundingClientRect().top;
    var offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth"
    });
  }

  setEConsent(){
    if(this.userSessionService.data.mhsApplication.econsent != undefined && this.userSessionService.data.mhsApplication.econsent != null){
      this.partialSaveService.updateEconsent();
    }
  }

  touchAllControls() {
    this.applicantInfoForm.markAllAsTouched();
    this.applicantBirthDateMonthLeaveEvent();
    this.applicantBirthDateDayLeaveEvent();
    this.applicantBirthDateYearLeaveEvent();
    this.applicantInfoForm.updateValueAndValidity();
  }

  validateRadioButtons(){
    if(this.applicantInfoForm.get('gender').hasError('required')){
      this.isGenderValid = false;
    } else {
      this.isGenderValid = true;
    }

    if(this.applicantInfoForm.get('placeOfBirth').hasError('required')){
      this.isBirthPlaceValid = false;
    } else {
      this.isBirthPlaceValid = true;
    }

    if(this.applicantInfoForm.get('communicationPreference').hasError('required')){
      this.isCommunicationValid = false;
    } else {
      this.isCommunicationValid = true;
    }
  }

  logGenderRadioChangeEvent(controlName: string){
    this.applicantInfoForm.get('gender').setValue(controlName);

    this.isGenderValid = true;
  }

  logBirthPlaceRadioChangeEvent(controlName: string){
    this.applicantInfoForm.get('placeOfBirth').setValue(controlName);

    this.isBirthPlaceValid = true;

    this.toggleBirthDropDown(controlName);
  }

  logCommunicationRadioChangeEvent(controlName: string){
    const control = this.applicantInfoForm.get('communicationPreference');
    control.setValue(controlName, { emitEvent: true });
    control.updateValueAndValidity();
    
    this.isCommunicationValid = true;
    
    // Force change detection to update UI immediately, even if form is pending
    this.cdr.detectChanges();
  }

  swapSSN(){
    this.applicantInfoForm.get("applicantSsn").setValue(this.actualSsnValue);
  }

  handleChangeEvent(event: Event): void {
    const customEvent = event as CustomEvent<InputEvent>;
    this.actualSsnValue = customEvent.detail.toString();
    this.applicantInfoForm.get('actualSsn').setValue(this.actualSsnValue);
  }

  fillInApplicationInfo(){

    let applicant = new ApplicantMhs();

    if(this.userSessionService.data.mhsApplication != undefined){

      applicant.firstName = this.applicantFirstName.value;
      applicant.lastName = this.applicantLastName.value;
      applicant.ssn = this.applicantSsn.value;
      applicant.gender = this.gender.value;
      applicant.email = this.email.value;
      applicant.communicationPreference = this.communicationPreference.value;
      applicant.relationshipToEmployee = this.userSessionService.data.applicantType;
      applicant.stateOfResidence = this.userSessionService.data.state;

      if(this.applicantInfoForm.contains('countryOfBirth')){
        applicant.countryOfBirth = this.countryOfBirth.value;
      } else {
        applicant.countryOfBirth = GlobalConstants.BIRTH_PLACE_US;
      }

      if(this.applicantInfoForm.contains('stateOfBirth')){
        applicant.stateOfBirth = this.stateOfBirth.value;
        applicant.countryOfBirth = GlobalConstants.BIRTH_PLACE_US;
      }

      applicant.countryOfResidence = this.country.value;
      applicant.placeOfBirth = this.placeOfBirth.value;
      applicant.enrollmentType = this.userSessionService.data.applicantType;
      applicant.birthDate = this.applicantInfoForm.get('applicantBirthDateYear').value + '-' +
          FormUtils.padWithZero(this.applicantInfoForm.get('applicantBirthDateMonth').value) + '-' +
          FormUtils.padWithZero(this.applicantInfoForm.get('applicantBirthDateDay').value);
      applicant.phone = this.applicantPhone.value;

      let address = new AddressMhs();

      address.line1 = this.applicantLine1.value;
      address.country = this.country.value;
      address.city = this.applicantCity.value;
      address.state = this.state.value;
      address.zip = this.applicantZipCode.value;

      applicant.address = address;

      if(this.userSessionService.data.applicantType?.toLowerCase() === "employee"){
        let employee: EmployeeMhs;
        employee = this.userSessionService.data.mhsApplication.employee;

        applicant.firstName = employee.firstName;
        applicant.lastName = employee.lastName;
        applicant.ssn = employee.ssn;
        applicant.birthDate = this.applicantInfoForm.get('applicantBirthDateYear').value + '-' +
          FormUtils.padWithZero(this.applicantInfoForm.get('applicantBirthDateMonth').value) + '-' +
          FormUtils.padWithZero(this.applicantInfoForm.get('applicantBirthDateDay').value);
      }

      applicant.coverage = this.userSessionService.data.mhsCoverages;

      this.userSessionService.data.mhsApplication.applicant = applicant;

    }
  }

  leaveAnyway(){
    this.userSessionService.clearSession();
    this.userSessionService.data.sessionEnded = true;
    this.authGuardService.cancelLeaveAnyway();
    this.router.navigate(['/instructions']);
  }

  onCancelModal(modalName: string){
    const modalElement =
      this.windowService.nativeDocument.getElementById(modalName);

    if (modalElement && modalElement.close) {
      modalElement.close();
    } else {
      this.appInsightsLoggerService.log('info', 'Info log from ApplicantInfo.component', `Modal with id ${modalName} not found or doesn't support close method.`);
    }
  }

  // use the window service to access the modal element
  // and close the modal when the user clicks outside of it
  onClick(event: Event) {
    const modal = this.windowService.nativeDocument.getElementById(
      'prism-ui-modal-cancel-modal'
    );

    if (modal && event.target === modal) {
      this.onCancelModal('prism-ui-modal-cancel-modal');
    }
  }

  onCancel(){
    this.cancelEmitter.emit();
  }

  onBack(){
    this.backEmitter.emit();
  }


  ssnMatchesEmployeeValidator() {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      let employee = new EmployeeMhs();
      employee = this.userSessionService.data.mhsApplication.employee;
      let applicantSsn = this.applicantInfoForm.get("actualSsn").value;
      if(employee.ssn === applicantSsn){
        this.hasSsnMatch = true;
        return {ssnMatchesEmployee:true}
      }
      return null;
    };
  }

  validateEmailAddress(): AsyncValidatorFn {
  return (email: AbstractControl): Promise<ValidationErrors | null> => {

    return new Promise((resolve) => {

      if (!email.value) {
        resolve(null);
        return;
      }

      if (!this.mhsService) {
        resolve(null);
        return;
      }

      this.mhsService.isValidEmailAddress(email.value).subscribe(
        (res) => {
          resolve(res ? null : { invalidEmail: true });
        },
        (err) => {
          this.log(
            'Invalid email address entered: ' +
            email.value +
            ' Error: ' +
            JSON.stringify(err)
          );
          resolve({ invalidEmail: true });
        }
      );

    });
  };
}

  canDeactivate(){
    if (!this.submitted && !this.userSessionService.data.sessionEnded){
      this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal
      return this.userSessionService.data.sessionEnded;
    } else {
      return of(true);
    }
  }

  isApplicantBirthDateValid() {
    return this.applicantInfoForm.get('applicantBirthDateMonth').valid &&
           this.applicantInfoForm.get('applicantBirthDateDay').valid &&
           this.applicantInfoForm.get('applicantBirthDateYear').valid;
  }

  applicantBirthDateMonthLeaveEvent(){
    this.applicantInfoForm.get('applicantBirthDateMonth').markAsTouched();
    this.applicantInfoForm.get('applicantBirthDateMonth').updateValueAndValidity();

    //validate month
    if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.applicantInfoForm, 'applicantBirthDateMonth')){
      if(this.formControlUtilitiesService.checkIfMonthControlIsBetweenOneAndTwelve(this.applicantInfoForm, 'applicantBirthDateMonth')){
        this.applicantInfoForm.get('applicantBirthDateMonth').setErrors(null);
      } else {
        this.applicantInfoForm.get('applicantBirthDateMonth').setErrors({ dateError: true });
        return;
      }
    } else {
      this.applicantInfoForm.get('applicantBirthDateMonth').setErrors({ dateError: true });
      return;
    }

    this.isDateofBirthFutureDated();
  }

  applicantBirthDateDayLeaveEvent(){
    this.applicantInfoForm.get('applicantBirthDateDay').markAsTouched();
    this.applicantInfoForm.get('applicantBirthDateDay').updateValueAndValidity();

    //validate day
    if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.applicantInfoForm, 'applicantBirthDateDay')){
      if(this.formControlUtilitiesService.checkIfDayControlIsBetweenOneAndThirtyOne(this.applicantInfoForm, 'applicantBirthDateDay')) { 
        this.applicantInfoForm.get('applicantBirthDateDay').setErrors(null);
      } else {
        this.applicantInfoForm.get('applicantBirthDateDay').setErrors({ dateError: true });
        return;
      }
    } else {
      this.applicantInfoForm.get('applicantBirthDateDay').setErrors({ dateError: true });
      return;
    }

    this.isDateofBirthFutureDated();
  }

  applicantBirthDateYearLeaveEvent(){
    this.applicantInfoForm.get('applicantBirthDateYear').markAsTouched();
    this.applicantInfoForm.get('applicantBirthDateYear').updateValueAndValidity();

    //validate year
    if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.applicantInfoForm, 'applicantBirthDateYear')) {
      if(this.formControlUtilitiesService.checkIfYearControlIsValid(this.applicantInfoForm, 'applicantBirthDateYear')) {
        this.applicantInfoForm.get('applicantBirthDateYear').setErrors(null);
      } else {
        this.applicantInfoForm.get('applicantBirthDateYear').setErrors({ dateError: true });
        return;
      }
    } else {
      this.applicantInfoForm.get('applicantBirthDateYear').setErrors({ dateError: true });
      return;
    }

    this.isDateofBirthFutureDated();
  }

  //can't future date dob
  isDateofBirthFutureDated(){
    let today = new Date();
    let tempDate = new Date(this.applicantInfoForm.get('applicantBirthDateYear').value, this.applicantInfoForm.get('applicantBirthDateMonth').value -1, this.applicantInfoForm.get('applicantBirthDateDay').value);
    if(tempDate > today){
      this.applicantInfoForm.get('applicantBirthDateMonth').setErrors({ dateError: true });
      this.applicantInfoForm.get('applicantBirthDateDay').setErrors({ dateError: true });
      this.applicantInfoForm.get('applicantBirthDateYear').setErrors({ dateError: true });
    } else {
      if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.applicantInfoForm, 'applicantBirthDateYear') &&
         this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.applicantInfoForm, 'applicantBirthDateMonth') &&
         this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.applicantInfoForm, 'applicantBirthDateDay')){

        this.applicantInfoForm.get('applicantBirthDateMonth').setErrors(null);
        this.applicantInfoForm.get('applicantBirthDateDay').setErrors(null);
        this.applicantInfoForm.get('applicantBirthDateYear').setErrors(null);

        // Display soft message: toast
        if (
          this.userSessionService.data.applicantType?.toLowerCase() ===
            ApplicantType.SPOUSE.toLowerCase() &&
          this.userSessionService.data.mhsApplication.employee.birthDate ===
            formatDate(tempDate, 'yyyy-MM-dd', 'en-US')
        ) {
          // Only show toast if it hasn't been shown before (check session storage)
          if (!this.dobToastShown && !this.userSessionService.data['dobToastShown']) {
            this.dobToast = true;
            this.dobToastShown = true;
            // Persist to session so it won't show again on navigation
            this.userSessionService.data['dobToastShown'] = true;
          }
        } else {
          // Reset toast flags if condition is no longer met
          this.dobToast = false;
          this.dobToastShown = false;
          this.userSessionService.data['dobToastShown'] = false;
        }
      }
    }
  }

  handlePrismUiToastOnClose(fieldName){
    // Close the toast and mark as shown permanently in session
    if (fieldName === 'applicantFirstName') {
      this.firstNameToast = false;
      this.firstNameToastShown = true;
      this.userSessionService.data['firstNameToastShown'] = true;
    } else if (fieldName === 'applicantBirthDateMonth' || fieldName === 'applicantBirthDateDay' || fieldName === 'applicantBirthDateYear') {
      this.dobToast = false;
      this.dobToastShown = true;
      this.userSessionService.data['dobToastShown'] = true;
    }
    
    const timeoutId = setTimeout(() => {
      const input = document.getElementById('prism-input-'+fieldName) as HTMLInputElement;
      input?.focus();
    }, 0);
    this.timeouts.push(timeoutId);

  }

  /**
   * Edit-mode "Back" button handler. Returns the user to /review
   * without saving any pending changes (matches the employee-info and coverage page behavior:
   * no confirmation modal because the destination is a review page, not an
   * exit from the application).
   */
  goBack(): void {
    if (this.isSavingEdit) {
      return;
    }
    // Discard any unsaved changes — do not overwrite the session cache.
    this.discardFormDataOnDestroy = true;
    // Mark as a safe navigation so the cancel-modal / leave-anyway flow is
    // bypassed when the deactivate guard fires.
    this.submitted = true;
    this.router.navigate([EEOI_APP_ROUTES.AUTH_ELEC_SIGN]);
  }

  /**
   * Edit-mode "Save" button handler. Validates the form and, if valid,
   * fills the applicant model, persists it to the backend, and navigates
   * back to /review with the success toast. On invalid form data
   * the existing field-level errors are surfaced via touchAllControls.
   */
  saveAndGoToReview(): void {
    // Defend against a double-click while a save is already in flight.
    if (this.isSavingEdit) {
      return;
    }

    this.googleAnalyticsService.sendCustomEvent(this.submitApplicantInfo);

    // Always normalize the SSN value before validating
    if(this.userSessionService.data.applicantType?.toLowerCase() !== "employee"){
      this.formControlUtilitiesService.ssnLeaveEvent(this.applicantInfoForm , 'applicantSsn', this.hasSsnMatch);
    }

    this.touchAllControls();
    this.validateRadioButtons();
    
    if(this.userSessionService.data.applicantType?.toLowerCase() === "employee"){
      if(this.userSessionService.data.mhsApplication.employee != undefined){
        this.copyEmployeeInfo();
      }
    }

    if (!this.applicantInfoForm.valid) {
      this.scrollToFirstInvalidControl();
      return;
    }

    // Set saving state BEFORE calling save to show button spinner
    this.isSavingEdit = true;
    this.cdr.detectChanges();
    
    const spinnerStartTime = new Date().getTime();
    
    this.appInsightsLoggerService.log('info', 'ApplicantInfoComponent: Edit-mode save started.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');

    // Push the form values into the session-level applicant model
    this.swapSSN();
    this.fillInApplicationInfo();
    
    // Save to backend
    this.savePartialApplication(spinnerStartTime);
  }

  /**
   * Common post-save navigation: flip the success-toast flag and route back
   * to /review. Mirrors the employee-info and coverage page's helper of the same name
   * so the user sees the same success banner regardless of which section
   * they edited.
   */
  private navigateBackToReviewAfterSave(): void {
    this.userSessionService.data['showCoverageSavedToast'] = true;
    sessionStorage.setItem('showCoverageSavedToast', 'true');

    const destinationRoute = EEOI_APP_ROUTES.AUTH_ELEC_SIGN;
    this.submitted = true;
    this.router.navigate([destinationRoute], { state: { showSuccess: true } });
    this.appInsightsApiService.startNavigationEvent(destinationRoute.toString());
  }
}
